import { useEffect, useState } from "react";
import { toast } from "sonner";
import { CreditCard, Save, ShieldCheck } from "lucide-react";
import { adminGetWiPay, adminSaveWiPay } from "@/lib/api";

const inputCls =
  "w-full border border-white/15 bg-ink px-4 py-3 text-sm text-white placeholder-zinc-600 outline-none transition-colors focus:border-brand-yellow";

const AdminWiPay = ({ adminKey }) => {
  const [accountNumber, setAccountNumber] = useState("");
  const [apiKey, setApiKey] = useState("");
  const [env, setEnv] = useState("sandbox");
  const [loaded, setLoaded] = useState(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    adminGetWiPay(adminKey)
      .then((d) => {
        setLoaded(d);
        setAccountNumber(d.account_number || "");
        setEnv(d.env || "sandbox");
      })
      .catch(() => toast.error("Could not load payment settings"));
  }, [adminKey]);

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await adminSaveWiPay({ account_number: accountNumber, api_key: apiKey, env }, adminKey);
      toast.success("WiPay settings saved", { description: "Live after your next order" });
      setLoaded({ account_number: accountNumber, env, api_key_set: true, source: "database" });
      setApiKey("");
    } catch {
      toast.error("Save failed");
    } finally {
      setSaving(false);
    }
  };

  return (
    <div data-testid="admin-wipay-panel" className="max-w-xl border border-white/10 bg-ink-3">
      <div className="h-1 cmyk-line" />
      <form onSubmit={save} className="p-6">
        <div className="flex items-center gap-3">
          <CreditCard className="h-6 w-6 text-brand-yellow" />
          <div>
            <h3 className="font-display text-xl font-black uppercase text-white">WiPay Jamaica</h3>
            <p className="font-mono text-[10px] uppercase tracking-widest text-zinc-500">
              Hosted card checkout • jm.wipayfinancial.com
            </p>
          </div>
        </div>

        {loaded && (
          <p
            data-testid="admin-wipay-status"
            className="mt-4 border border-white/10 bg-ink px-3 py-2 font-mono text-[10px] uppercase tracking-widest text-zinc-400"
          >
            Active config: {loaded.env} •{" "}
            {loaded.api_key_set ? "API key stored" : "no API key"} • source: {loaded.source}
          </p>
        )}

        <label className="mt-5 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          WiPay Merchant ID (Account Number)
        </label>
        <input
          data-testid="admin-wipay-account"
          value={accountNumber}
          onChange={(e) => setAccountNumber(e.target.value)}
          placeholder="Paste after corporate verification"
          className={`${inputCls} mt-1.5`}
        />

        <label className="mt-4 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          WiPay API Token (stored server-side, never displayed)
        </label>
        <input
          data-testid="admin-wipay-token"
          type="password"
          autoComplete="new-password"
          value={apiKey}
          onChange={(e) => setApiKey(e.target.value)}
          placeholder="Paste API token"
          className={`${inputCls} mt-1.5`}
        />

        <label className="mt-4 block font-mono text-[10px] uppercase tracking-widest text-zinc-500">
          Environment
        </label>
        <select
          data-testid="admin-wipay-env"
          value={env}
          onChange={(e) => setEnv(e.target.value)}
          className={`${inputCls} mt-1.5`}
        >
          <option value="sandbox">Sandbox (testing)</option>
          <option value="live">Live (production)</option>
        </select>

        <button
          data-testid="admin-wipay-save"
          type="submit"
          disabled={saving || !accountNumber.trim() || !apiKey.trim()}
          className="mt-5 flex w-full items-center justify-center gap-2 bg-brand-yellow py-3.5 font-display text-sm font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan disabled:opacity-40"
        >
          <Save className="h-4 w-4" /> {saving ? "Saving…" : "Save WiPay Settings"}
        </button>
        <p className="mt-3 flex items-center gap-2 font-mono text-[9px] uppercase tracking-wider text-zinc-600">
          <ShieldCheck className="h-3.5 w-3.5" /> Token is write-only and stored on the server
        </p>
      </form>
    </div>
  );
};

export default AdminWiPay;
